import { useMemo, useState } from "react";
import { Calendar, Columns3, Plus, Trash2, FlaskConical, CheckCircle2, GalleryHorizontalEnd, ClipboardCopy, Sparkle, X, CalendarPlus } from "lucide-react";
import { Tag, SecondaryButton } from "./kit";

const STATUS = {
  idea: { color: "neutral", label: "IDEA" },
  script_ready: { color: "blue", label: "SCRIPT READY" },
  published: { color: "green", label: "PUBLISHED" },
};
export const PILLAR_COLORS = ["rose", "blue", "green", "amber", "lilac", "neutral"];
const pillarColor = (name, pillars) => {
  const i = (pillars || []).indexOf(name);
  return i === -1 ? "neutral" : PILLAR_COLORS[i % PILLAR_COLORS.length];
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => d.toISOString().slice(0, 10);
const todayIso = () => iso(new Date());

const SectionLabel = ({ children, className = "" }) => (
  <p className={`text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase ${className}`}>{children}</p>
);

const PillarDot = ({ color }) => (
  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: `var(--${color}-fg)` }} />
);

const SlotCard = ({ slot, pillars, onMove, onStatus, onDelete, onScript, onCarousel, onCaption, onCaptionSave, captionBusyId, busy }) => (
  <div className="card-soft p-4 overflow-hidden relative" data-testid={`slot-card-${slot.id}`}>
    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: `var(--${pillarColor(slot.pillar, pillars)}-fg)` }} />
    <div className="flex flex-wrap items-center justify-between gap-2 pl-1">
      <div className="flex items-center gap-2 min-w-0 flex-wrap">
        <input
          type="date"
          value={slot.date}
          onChange={(e) => e.target.value && onMove(slot.id, e.target.value)}
          className="field-input !w-[8.5rem] !py-1.5 !px-2 text-[12px] tabular-nums"
          data-testid={`slot-date-${slot.id}`}
        />
        {slot.pillar && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EAE5DB] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#181621]" data-testid={`slot-pillar-${slot.id}`}>
            <PillarDot color={pillarColor(slot.pillar, pillars)} /> {slot.pillar}
          </span>
        )}
        <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-[#FBFAF7] px-2.5 py-0.5 text-[11px] font-semibold text-[#54505B]">{slot.tipo}</span>
        {slot.content_type === "carousel" && (
          <span className="inline-flex items-center gap-1 rounded-full border border-[#EAE5DB] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#54505B]" data-testid={`slot-carousel-badge-${slot.id}`}>
            <GalleryHorizontalEnd size={10} strokeWidth={2} /> Carousel
          </span>
        )}
        <Tag color={STATUS[slot.status].color}>{STATUS[slot.status].label}</Tag>
      </div>
      <button onClick={() => onDelete(slot.id)} className="text-[#9A9591] hover:text-[#181621]" data-testid={`slot-delete-${slot.id}`}>
        <Trash2 size={14} strokeWidth={1.8} />
      </button>
    </div>
    <p className="font-bold text-[14px] mt-2.5 pl-1">{slot.title}</p>
    {slot.punti && <p className="text-[12px] text-[#54505B] mt-1 pl-1">{slot.punti}</p>}
    <div className="flex gap-2 mt-3.5 flex-wrap pl-1">
      <SecondaryButton className="!py-1.5 !px-3 text-[12px]" disabled={busy} onClick={() => onScript(slot)} data-testid={`slot-script-${slot.id}`}>
        <FlaskConical size={12} strokeWidth={1.8} /> {slot.status === "idea" ? "Turn into script" : "Regenerate script"}
      </SecondaryButton>
      <SecondaryButton className="!py-1.5 !px-3 text-[12px]" disabled={busy} onClick={() => onCarousel(slot)} data-testid={`slot-carousel-${slot.id}`}>
        <GalleryHorizontalEnd size={12} strokeWidth={1.8} /> {slot.content_type === "carousel" ? "Open carousel" : "Turn into carousel"}
      </SecondaryButton>
      {slot.status !== "published" ? (
        <SecondaryButton className="!py-1.5 !px-3 text-[12px]" onClick={() => onStatus(slot.id, "published")} data-testid={`slot-publish-${slot.id}`}>
          <CheckCircle2 size={12} strokeWidth={1.8} /> Mark published
        </SecondaryButton>
      ) : (
        <SecondaryButton className="!py-1.5 !px-3 text-[12px]" onClick={() => onStatus(slot.id, slot.history_id ? "script_ready" : "idea")} data-testid={`slot-unpublish-${slot.id}`}>
          Undo publish
        </SecondaryButton>
      )}
    </div>
    {(onCaption || slot.caption) && (
      <div className="mt-3.5 pt-3 border-t border-[#F3EFE7] pl-1" data-testid={`slot-caption-block-${slot.id}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">Caption</span>
          <div className="flex items-center gap-2">
            {slot.caption && (
              <button onClick={() => { navigator.clipboard?.writeText(slot.caption); }} className="text-[#9A9591] hover:text-[#181621] text-[11px] font-bold inline-flex items-center gap-1" data-testid={`slot-caption-copy-${slot.id}`}>
                <ClipboardCopy size={12} strokeWidth={1.8} /> Copy
              </button>
            )}
            {onCaption && (
              <button onClick={() => onCaption(slot)} disabled={captionBusyId === slot.id} className="text-[#54505B] hover:text-[#181621] text-[11px] font-bold disabled:opacity-50" data-testid={`slot-caption-generate-${slot.id}`}>
                {captionBusyId === slot.id ? "Writing…" : slot.caption ? "Rewrite" : "Write caption"}
              </button>
            )}
          </div>
        </div>
        {slot.caption ? (
          <textarea rows={4} className="field-input !text-[12.5px] w-full mt-2 resize-y" defaultValue={slot.caption}
            onBlur={(e) => onCaptionSave && e.target.value !== slot.caption && onCaptionSave(slot.id, e.target.value)}
            data-testid={`slot-caption-text-${slot.id}`} />
        ) : (
          <p className="text-[11.5px] text-[#9A9591] mt-1.5">{captionBusyId === slot.id ? "Writing a ready-to-publish caption…" : "No caption yet."}</p>
        )}
      </div>
    )}
  </div>
);

const MomentCard = ({ moment, onAccept, onDismiss, onScript, onCarousel, busyKey }) => {
  const busy = busyKey === moment.key;
  return (
    <div className="card-soft p-4 border-[#E9DCC3]" data-testid={`moment-card-${moment.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-7 h-7 rounded-lg bg-[#FCF6EC] border border-[#E9DCC3] flex items-center justify-center shrink-0">
            <Sparkle size={13} strokeWidth={2} className="text-[#B08A45]" />
          </span>
          <span className="text-[11px] font-semibold text-[#8A6A3F] tabular-nums">{moment.date}</span>
          <span className="inline-flex items-center rounded-full border border-[#E9DCC3] bg-white px-2.5 py-0.5 text-[10.5px] font-bold text-[#8A6A3F] uppercase tracking-[0.06em]">Suggested</span>
        </div>
        <button onClick={() => onDismiss(moment.key)} disabled={busy} className="text-[#9A9591] hover:text-[#181621] disabled:opacity-50" title="Dismiss" data-testid={`moment-dismiss-${moment.key}`}>
          <X size={14} strokeWidth={1.8} />
        </button>
      </div>
      <p className="font-bold text-[14px] mt-2.5">{moment.name}</p>
      {moment.angle && <p className="text-[12px] text-[#54505B] mt-1" data-testid={`moment-angle-${moment.key}`}>{moment.angle}</p>}
      <div className="flex gap-2 mt-3.5 flex-wrap">
        <SecondaryButton className="!py-1.5 !px-3 text-[12px]" disabled={busy} onClick={() => onAccept(moment)} data-testid={`moment-accept-${moment.key}`}>
          <CalendarPlus size={12} strokeWidth={1.8} /> {busy ? "Adding…" : "Add to calendar"}
        </SecondaryButton>
        <SecondaryButton className="!py-1.5 !px-3 text-[12px]" disabled={busy} onClick={() => onScript(moment)} data-testid={`moment-script-${moment.key}`}>
          <FlaskConical size={12} strokeWidth={1.8} /> Turn into script
        </SecondaryButton>
        <SecondaryButton className="!py-1.5 !px-3 text-[12px]" disabled={busy} onClick={() => onCarousel(moment)} data-testid={`moment-carousel-${moment.key}`}>
          <GalleryHorizontalEnd size={12} strokeWidth={1.8} /> Turn into carousel
        </SecondaryButton>
      </div>
    </div>
  );
};

const mondayOf = (d) => {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const off = (x.getUTCDay() + 6) % 7;
  x.setUTCDate(x.getUTCDate() - off);
  return x;
};

export const EditorialCalendar = ({ calendar, pillars = [], moments = [], onMomentAccept, onMomentDismiss, onMomentScript, onMomentCarousel, momentBusyKey, onCadence, onMove, onStatus, onDelete, onScript, onCarousel, onAddSlot, onCaption, onCaptionSave, captionBusyId, busy }) => {
  const [view, setView] = useState("month");
  const [month, setMonth] = useState(() => { const d = new Date(); return [d.getFullYear(), d.getMonth()]; });
  const [weekOffset, setWeekOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const [selectedMoment, setSelectedMoment] = useState(null);
  const [custom, setCustom] = useState({ title: "", date: "" });
  const today = todayIso();

  const byDate = useMemo(() => {
    const m = {};
    calendar.slots.forEach((s) => { (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [calendar.slots]);

  const momentsByDate = useMemo(() => {
    const m = {};
    moments.forEach((mm) => { (m[mm.date] = m[mm.date] || []).push(mm); });
    return m;
  }, [moments]);

  const cells = useMemo(() => {
    const [y, mo] = month;
    const first = new Date(Date.UTC(y, mo, 1));
    const lead = (first.getUTCDay() + 6) % 7;
    const days = new Date(y, mo + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => iso(new Date(Date.UTC(y, mo, i + 1))))];
  }, [month]);

  const weekDates = useMemo(() => {
    const start = mondayOf(new Date());
    start.setUTCDate(start.getUTCDate() + weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setUTCDate(d.getUTCDate() + i);
      return iso(d);
    });
  }, [weekOffset]);

  const selectedSlot = calendar.slots.find((s) => s.id === selected);
  const selectedMomentObj = moments.find((m) => m.key === selectedMoment);

  const SlotChip = ({ s, mini = false }) => (
    <button
      onClick={() => { setSelectedMoment(null); setSelected(s.id === selected ? null : s.id); }}
      className={`block w-full text-left mt-1 px-1.5 py-1 rounded-md text-[10px] leading-tight font-semibold truncate border transition-colors ${selected === s.id ? "border-[#181621]" : "border-transparent"}`}
      style={{ background: `var(--${STATUS[s.status].color}-bg)`, color: `var(--${STATUS[s.status].color}-fg)` }}
      title={s.pillar ? `${s.pillar} — ${s.title}` : s.title}
      data-testid={`month-chip-${s.id}`}
    >
      {s.pillar && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: `var(--${pillarColor(s.pillar, pillars)}-fg)` }} data-testid={`month-chip-pillar-dot-${s.id}`} />}
      {s.title}
    </button>
  );

  const MomentChip = ({ m }) => (
    <button
      onClick={() => { setSelected(null); setSelectedMoment(m.key === selectedMoment ? null : m.key); }}
      className={`block w-full text-left mt-1 px-1.5 py-1 rounded-md text-[10px] leading-tight font-semibold truncate border bg-[#FCF6EC] text-[#8A6A3F] transition-colors ${selectedMoment === m.key ? "border-[#181621]" : "border-[#E9DCC3]"}`}
      title={`Suggested — ${m.name}`}
      data-testid={`moment-chip-${m.key}`}
    >
      <Sparkle size={9} strokeWidth={2} className="inline mr-1 -mt-0.5" />
      {m.name}
    </button>
  );

  return (
    <div className="mt-6" data-testid="editorial-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <SectionLabel>Publishing calendar</SectionLabel>
          {moments.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E9DCC3] bg-[#FCF6EC] px-3 py-1 text-[11px] font-bold text-[#8A6A3F] tabular-nums" data-testid="moments-count-tag">
              <Sparkle size={11} strokeWidth={2} className="text-[#B08A45]" /> {moments.length} marketing moment{moments.length > 1 ? "s" : ""} suggested
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select className="field-input !w-auto !py-1.5 text-[12.5px]" value={calendar.cadence} onChange={(e) => onCadence(Number(e.target.value))} data-testid="cadence-select">
            <option value={2}>2 / week (Tue · Thu)</option>
            <option value={3}>3 / week (Mon · Wed · Fri)</option>
            <option value={5}>5 / week (Mon–Fri)</option>
          </select>
          <div className="inline-flex rounded-xl border border-[#EAE5DB] bg-white overflow-hidden">
            {[["month", Calendar, "Month"], ["week", Columns3, "Week"]].map(([v, Icon, label], i) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[12px] font-bold transition-colors ${i > 0 ? "border-l border-[#EAE5DB]" : ""} ${view === v ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"}`}
                data-testid={`view-${v}`}
              >
                <Icon size={13} strokeWidth={1.8} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card-soft p-4 mt-4 flex flex-wrap items-end gap-3" data-testid="custom-slot-form">
        <div className="flex-1 min-w-[160px]">
          <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">Custom idea</label>
          <input className="field-input !py-2" placeholder="Video title" value={custom.title} onChange={(e) => setCustom((p) => ({ ...p, title: e.target.value }))} data-testid="custom-slot-title" />
        </div>
        <input type="date" className="field-input !w-[9.5rem] !py-2 text-[12px] tabular-nums" value={custom.date} onChange={(e) => setCustom((p) => ({ ...p, date: e.target.value }))} data-testid="custom-slot-date" />
        <SecondaryButton className="!py-2 !px-4 text-[13px]" disabled={!custom.title.trim() || !custom.date} onClick={() => { onAddSlot(custom.title.trim(), custom.date); setCustom({ title: "", date: "" }); }} data-testid="custom-slot-add-btn">
          <Plus size={14} strokeWidth={1.8} /> Add
        </SecondaryButton>
      </div>

      {view === "month" ? (
        <div className="card-soft p-5 mt-4" data-testid="month-grid">
          <div className="flex items-center justify-between mb-4">
            <button className="rounded-lg border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12px] font-bold text-[#54505B] hover:border-[#181621] hover:text-[#181621] transition-colors" onClick={() => setMonth(([y, m]) => (m === 0 ? [y - 1, 11] : [y, m - 1]))} data-testid="month-prev">← prev</button>
            <span className="font-bold text-[14px] tabular-nums">{MONTHS[month[1]]} {month[0]}</span>
            <button className="rounded-lg border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12px] font-bold text-[#54505B] hover:border-[#181621] hover:text-[#181621] transition-colors" onClick={() => setMonth(([y, m]) => (m === 11 ? [y + 1, 0] : [y, m + 1]))} data-testid="month-next">next →</button>
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {DAYS.map((d) => (
              <span key={d} className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase text-center pb-1">{d}</span>
            ))}
            {cells.map((date, i) => {
              const isToday = date === today;
              return (
                <div key={i} className={`min-h-[72px] rounded-lg p-1.5 ${date ? (isToday ? "bg-white border border-[#181621]" : "bg-[#FAF8F3]") : ""}`} data-testid={isToday ? "today-cell" : undefined}>
                  {date && (
                    <>
                      <span className={`text-[10px] font-bold tabular-nums ${isToday ? "inline-flex items-center justify-center w-4.5 h-4.5 px-1 rounded-full bg-[#E0603A] text-white" : "text-[#9A9591]"}`}>{Number(date.slice(8))}</span>
                      {(byDate[date] || []).map((s) => <SlotChip key={s.id} s={s} />)}
                      {(momentsByDate[date] || []).map((m) => <MomentChip key={m.key} m={m} />)}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {selectedSlot && (
            <div className="mt-4" data-testid="selected-slot">
              <SlotCard slot={selectedSlot} pillars={pillars} onMove={onMove} onStatus={onStatus} onDelete={(id) => { onDelete(id); setSelected(null); }} onScript={onScript} onCarousel={onCarousel} onCaption={onCaption} onCaptionSave={onCaptionSave} captionBusyId={captionBusyId} busy={busy} />
            </div>
          )}
          {selectedMomentObj && (
            <div className="mt-4" data-testid="selected-moment">
              <MomentCard moment={selectedMomentObj} onAccept={onMomentAccept} onDismiss={(k) => { onMomentDismiss(k); setSelectedMoment(null); }} onScript={onMomentScript} onCarousel={onMomentCarousel} busyKey={momentBusyKey} />
            </div>
          )}
        </div>
      ) : (
        <div className="card-soft p-5 mt-4" data-testid="week-list">
          <div className="flex items-center justify-between mb-4">
            <button className="rounded-lg border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12px] font-bold text-[#54505B] hover:border-[#181621] hover:text-[#181621] transition-colors" onClick={() => setWeekOffset((w) => w - 1)} data-testid="week-prev">← prev</button>
            <span className="font-bold text-[14px] tabular-nums">
              {new Date(weekDates[0]).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {new Date(weekDates[6]).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              {weekOffset === 0 && <span className="ml-2 text-[11px] font-semibold text-[#E0603A]">this week</span>}
            </span>
            <button className="rounded-lg border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12px] font-bold text-[#54505B] hover:border-[#181621] hover:text-[#181621] transition-colors" onClick={() => setWeekOffset((w) => w + 1)} data-testid="week-next">next →</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-1.5">
            {weekDates.map((date, i) => {
              const isToday = date === today;
              const daySlots = byDate[date] || [];
              const dayMoments = momentsByDate[date] || [];
              return (
                <div key={date} className={`rounded-lg p-2 min-h-[120px] ${isToday ? "bg-white border border-[#181621]" : "bg-[#FAF8F3]"}`} data-testid={`week-day-${date}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">{DAYS[i]}</span>
                    <span className={`text-[10.5px] font-bold tabular-nums ${isToday ? "inline-flex items-center justify-center px-1.5 rounded-full bg-[#E0603A] text-white" : "text-[#9A9591]"}`}>{Number(date.slice(8))}</span>
                  </div>
                  <div className="mt-1">
                    {daySlots.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => { setSelectedMoment(null); setSelected(s.id === selected ? null : s.id); }}
                        className={`block w-full text-left mt-1.5 rounded-lg border bg-white p-2 transition-colors ${selected === s.id ? "border-[#181621]" : "border-[#EAE5DB] hover:border-[#9A9591]"}`}
                        data-testid={`week-slot-${s.id}`}
                      >
                        <span className="flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-6 rounded-full shrink-0" style={{ background: `var(--${pillarColor(s.pillar, pillars)}-fg)` }} />
                          <span className="min-w-0">
                            <span className="block text-[9px] font-bold uppercase tracking-[0.06em]" style={{ color: `var(--${STATUS[s.status].color}-fg)` }}>{STATUS[s.status].label}</span>
                            <span className="block text-[11px] font-bold text-[#181621] leading-tight line-clamp-2">{s.title}</span>
                          </span>
                        </span>
                      </button>
                    ))}
                    {dayMoments.map((m) => (
                      <button
                        key={m.key}
                        onClick={() => { setSelected(null); setSelectedMoment(m.key === selectedMoment ? null : m.key); }}
                        className={`block w-full text-left mt-1.5 rounded-lg border bg-[#FCF6EC] p-2 transition-colors ${selectedMoment === m.key ? "border-[#181621]" : "border-[#E9DCC3] hover:border-[#B08A45]"}`}
                        data-testid={`week-moment-${m.key}`}
                      >
                        <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.06em] text-[#8A6A3F]">
                          <Sparkle size={9} strokeWidth={2} /> Suggested
                        </span>
                        <span className="block text-[11px] font-bold text-[#181621] leading-tight line-clamp-2 mt-0.5">{m.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {calendar.slots.length === 0 && moments.length === 0 && (
            <p className="text-center text-[13px] text-[#54505B] mt-5" data-testid="calendar-empty">No slots yet. Generate 10 ideas or add a custom one.</p>
          )}
          {selectedSlot && (
            <div className="mt-4" data-testid="selected-slot">
              <SlotCard slot={selectedSlot} pillars={pillars} onMove={onMove} onStatus={onStatus} onDelete={(id) => { onDelete(id); setSelected(null); }} onScript={onScript} onCarousel={onCarousel} onCaption={onCaption} onCaptionSave={onCaptionSave} captionBusyId={captionBusyId} busy={busy} />
            </div>
          )}
          {selectedMomentObj && (
            <div className="mt-4" data-testid="selected-moment">
              <MomentCard moment={selectedMomentObj} onAccept={onMomentAccept} onDismiss={(k) => { onMomentDismiss(k); setSelectedMoment(null); }} onScript={onMomentScript} onCarousel={onMomentCarousel} busyKey={momentBusyKey} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
